import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useHasRole } from '@/hooks/useHasRole';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAdminUserSearch, type AdminUserSegment, type AdminUserRow } from '@/hooks/useAdminUsers';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, ArrowUpDown, ChevronRight, Mail, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ACCOUNT_LABELS, DEFAULT_LEAD_FILTERS, LeadFilters, STEP_LABELS,
  applyLeadFilters, type LeadFilterState, type SortKey,
} from '@/components/admin/LeadFilters';
import { BulkNudgeDialog } from '@/components/admin/BulkNudgeDialog';

const PERIODS = [7, 30, 90] as const;

const SEGMENTS: Array<{ key: AdminUserSegment; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'unconfirmed', label: 'E-mail não confirmado' },
  { key: 'no_patient', label: 'Sem paciente' },
  { key: 'no_plan', label: 'Sem plano de cuidado' },
  { key: 'no_shift', label: 'Sem plantão' },
  { key: 'active', label: 'Ativos no período' },
  { key: 'inactive_14', label: 'Inativos 14d+' },
  { key: 'free', label: 'Free com paciente' },
  { key: 'paying', label: 'Assinantes' },
];

function fmt(iso: string | null) {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'dd/MM/yy', { locale: ptBR });
  } catch {
    return '—';
  }
}

function InactivityBadge({ days }: { days: number | null }) {
  if (days === null) return <Badge variant="outline">nunca</Badge>;
  if (days <= 3) return <Badge className="bg-success/15 text-success hover:bg-success/15">{days}d</Badge>;
  if (days <= 13) return <Badge className="bg-warning/15 text-warning hover:bg-warning/15">{days}d</Badge>;
  return <Badge className="bg-destructive/15 text-destructive hover:bg-destructive/15">{days}d</Badge>;
}

function SubBadge({ status, label, env }: { status: string; label: string; env?: string | null }) {
  const active = ['active', 'trialing'].includes(status);
  return (
    <div className="flex items-center gap-1">
      <Badge
        className={active ? 'bg-success/15 text-success hover:bg-success/15' : undefined}
        variant={active ? undefined : 'outline'}
      >
        <span className={active ? '' : 'text-muted-foreground'}>{label}: {status}</span>
      </Badge>
      {env === 'test' && <span className="text-[10px] text-muted-foreground">test</span>}
    </div>
  );
}

function SubCell({ r }: { r: AdminUserRow }) {
  const personal = r.personal_subscription_status ?? null;
  const company = r.company_subscription_status ?? null;
  if (!personal && !company) {
    if (r.subscription_status) {
      return <SubBadge status={r.subscription_status} label="Assinatura" />;
    }
    return <Badge variant="outline">free</Badge>;
  }
  return (
    <div className="flex flex-col gap-1 items-start">
      {personal && <SubBadge status={personal} label="Familiar" env={r.personal_subscription_env} />}
      {company && <SubBadge status={company} label="Empresa" env={r.company_subscription_env} />}
    </div>
  );
}

function SortHead({
  label, sortKey, filters, onChange, className,
}: {
  label: string;
  sortKey: SortKey;
  filters: LeadFilterState;
  onChange: (f: LeadFilterState) => void;
  className?: string;
}) {
  const active = filters.sortKey === sortKey;
  return (
    <TableHead className={className}>
      <button
        type="button"
        className={`inline-flex items-center gap-1 ${active ? 'text-foreground font-medium' : ''}`}
        onClick={() =>
          onChange({
            ...filters,
            sortKey,
            sortDir: active && filters.sortDir === 'desc' ? 'asc' : 'desc',
          })
        }
      >
        {label}
        <ArrowUpDown className="h-3 w-3 opacity-60" />
      </button>
    </TableHead>
  );
}

export function AdminUsersContent({ periodDays, refreshKey }: { periodDays?: number; refreshKey?: number } = {}) {
  const [localPeriod, setLocalPeriod] = useState<number>(30);
  const period = periodDays ?? localPeriod;
  const [segment, setSegment] = useState<AdminUserSegment>('all');
  const [filters, setFilters] = useState<LeadFilterState>({ ...DEFAULT_LEAD_FILTERS });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading, isError, refetch, isRefetching } = useAdminUserSearch({
    query: '',
    segment,
    periodDays: period,
    limit: 200,
  });

  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0) {
      refetch();
    }
  }, [refreshKey, refetch]);

  const rows = useMemo(() => applyLeadFilters(data?.users ?? [], filters), [data?.users, filters]);

  useEffect(() => {
    // Drop selections that fell out of the current filter/segment.
    setSelected((prev) => {
      const visible = new Set(rows.map((r) => r.user_id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.user_id)), [rows, selected]);
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;

  const outreachStats = useMemo(() => {
    const sent = rows.filter((r) => r.last_outreach_at).length;
    const clicked = rows.filter((r) => r.last_outreach_clicked).length;
    return { sent, clicked, rate: sent ? Math.round((clicked / sent) * 100) : null };
  }, [rows]);

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {periodDays === undefined &&
            PERIODS.map((p) => (
              <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'} onClick={() => setLocalPeriod(p)}>
                {p} dias
              </Button>
            ))}
          {periodDays === undefined && (
            <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isRefetching}>
              <RefreshCw className={`h-4 w-4 mr-1 ${isRefetching ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          )}
        </div>

        <div className="overflow-x-auto scrollbar-none -mx-4 px-4">
          <div className="flex gap-2 w-max">
            {SEGMENTS.map((s) => (
              <Button
                key={s.key}
                size="sm"
                variant={segment === s.key ? 'default' : 'outline'}
                onClick={() => setSegment(s.key)}
                className="whitespace-nowrap"
              >
                {s.label}
                {data?.counts?.[s.key] !== undefined && (
                  <span className="ml-1 text-xs opacity-70">{data.counts[s.key]}</span>
                )}
              </Button>
            ))}
          </div>
        </div>

        <LeadFilters
          value={filters}
          onChange={setFilters}
          resultCount={rows.length}
          totalCount={data?.users.length ?? 0}
        />

        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-3 text-sm">
            <span className="text-muted-foreground">
              {outreachStats.sent} lead(s) já receberam estímulo
              {outreachStats.rate !== null && ` · ${outreachStats.rate}% clicaram no CTA`}
            </span>
            <div className="flex-1" />
            <span className="text-muted-foreground text-xs">
              {selectedRows.length} selecionado(s)
            </span>
            <Button size="sm" disabled={selectedRows.length === 0} onClick={() => setBulkOpen(true)}>
              <Mail className="h-4 w-4 mr-1" />
              Enviar estímulo
            </Button>
          </CardContent>
        </Card>

        {isError && (
          <Card>
            <CardContent className="p-4 text-sm text-destructive">
              Erro ao carregar os usuários. Verifique se você tem o papel <code>careos_admin</code>.
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : data ? (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto scrollbar-none">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allSelected}
                          aria-label="Selecionar todos os leads filtrados"
                          onCheckedChange={(v) =>
                            setSelected(v ? new Set(rows.map((r) => r.user_id)) : new Set())
                          }
                        />
                      </TableHead>
                      <SortHead label="Usuário" sortKey="full_name" filters={filters} onChange={setFilters} />
                      <TableHead>Tipo</TableHead>
                      <TableHead>Etapa travada</TableHead>
                      <SortHead label="Parado" sortKey="days_stuck" filters={filters} onChange={setFilters} className="text-center" />
                      <SortHead label="Inatividade" sortKey="days_inactive" filters={filters} onChange={setFilters} className="text-center" />
                      <TableHead>Último estímulo</TableHead>
                      <TableHead>Assinatura</TableHead>
                      <SortHead label="Cadastro" sortKey="created_at" filters={filters} onChange={setFilters} />
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r: AdminUserRow) => (
                      <TableRow key={r.user_id} data-state={selected.has(r.user_id) ? 'selected' : undefined}>
                        <TableCell>
                          <Checkbox
                            checked={selected.has(r.user_id)}
                            aria-label={`Selecionar ${r.email ?? r.user_id}`}
                            onCheckedChange={() => toggleRow(r.user_id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Link to={`/app/admin/users/${r.user_id}`} className="block">
                            <span className="font-medium text-sm block truncate max-w-[220px]">
                              {r.full_name || '—'}
                            </span>
                            <span className="text-xs text-muted-foreground block truncate max-w-[220px]">
                              {r.email}
                            </span>
                            {r.email_confirmed === false && (
                              <span className="mt-1 flex items-center gap-1 flex-wrap">
                                <Badge className="bg-destructive/15 text-destructive hover:bg-destructive/15">
                                  E-mail não confirmado
                                </Badge>
                                {r.last_email_status && (
                                  <span className="text-[10px] text-muted-foreground">
                                    último e-mail: {r.last_email_status}
                                  </span>
                                )}
                              </span>
                            )}
                          </Link>
                        </TableCell>
                        <TableCell className="text-xs">
                          {ACCOUNT_LABELS[r.account_type] ?? r.account_type}
                          {r.company_name && (
                            <span className="block text-muted-foreground truncate max-w-[160px]">{r.company_name}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.blocked_step ? (
                            <Badge variant="outline" className="whitespace-nowrap">
                              {STEP_LABELS[r.blocked_step] ?? r.blocked_step}
                            </Badge>
                          ) : (
                            <span className="text-success">funil completo</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {r.days_stuck !== null && r.days_stuck !== undefined ? `${r.days_stuck}d` : '—'}
                        </TableCell>
                        <TableCell className="text-center"><InactivityBadge days={r.days_inactive} /></TableCell>
                        <TableCell className="text-xs">
                          {r.last_outreach_at ? (
                            <span className="flex flex-col">
                              <span className="text-muted-foreground">{fmt(r.last_outreach_at)}</span>
                              {r.last_outreach_clicked ? (
                                <span className="text-success">clicou no CTA</span>
                              ) : (
                                <span className="text-muted-foreground">sem clique</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell><SubCell r={r} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{fmt(r.created_at)}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" asChild>
                            <Link to={`/app/admin/users/${r.user_id}`}>
                              <ChevronRight className="h-4 w-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-6">
                          Nenhum lead encontrado para estes filtros.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {data && (
          <p className="text-xs text-muted-foreground">
            {rows.length} lead(s) exibidos · {data.total} no segmento selecionado.
          </p>
        )}
      </div>

      <BulkNudgeDialog open={bulkOpen} onOpenChange={setBulkOpen} leads={selectedRows} />
    </>
  );
}

export default function AdminUsersPage() {
  const isAdmin = useHasRole('careos_admin');

  if (isAdmin === null) {
    return (
      <AppLayout title="Usuários">
        <div className="p-4"><Skeleton className="h-12 w-48" /></div>
      </AppLayout>
    );
  }
  if (isAdmin === false) return <Navigate to="/app" replace />;

  return (
    <AppLayout title="Usuários">
      <div className="container mx-auto p-4 space-y-4 max-w-7xl">
        <div>
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/admin"><ArrowLeft className="h-4 w-4 mr-1" />Painel</Link>
          </Button>
        </div>
        <AdminUsersContent />
      </div>
    </AppLayout>
  );
}
